import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Share2, Copy, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Social share row.
 * Pass `shareUrl` from the parent (built via lib/share.js — buildListingShareUrl
 * or buildShopShareUrl) so that pasting it into Facebook/WhatsApp/Telegram/X
 * yields a rich preview with image + title + price + description.
 *
 * Falls back to window.location.href for backward compatibility, but that
 * produces the generic site preview — always prefer the OG URL.
 */
export default function ShareButtons({ title, url, shareUrl }) {
  const { language } = useLanguage();
  const [copied, setCopied] = React.useState(false);
  const actualShareUrl = shareUrl || url || window.location.href;
  const shareText = title || 'KoLV 2026';

  const handleCopy = async () => {
    try {
      // Try native share first (mobile gets the OS picker).
      if (navigator.share) {
        try {
          await navigator.share({ url: actualShareUrl, title: shareText, text: shareText });
          return;
        } catch (e) {
          if (e && e.name === 'AbortError') return; // user cancelled
        }
      }
      await navigator.clipboard.writeText(actualShareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { console.error(e); }
  };

  const shareLinks = [
    {
      name: 'Facebook',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(actualShareUrl)}`,
      color: 'hover:bg-[#1877F2] hover:text-white',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
        </svg>
      )
    },
    {
      name: 'WhatsApp',
      url: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${actualShareUrl}`)}`,
      color: 'hover:bg-[#25D366] hover:text-white',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      )
    },
    {
      name: 'X',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(actualShareUrl)}`,
      color: 'hover:bg-[#121212] hover:text-white',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      )
    }
  ];

  return (
    <div className="flex items-center gap-2" data-testid="share-buttons">
      {shareLinks.map(link => (
        <a
          key={link.name}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`w-9 h-9 rounded-full border border-[#E5E4E0] flex items-center justify-center text-[#5C5C5C] transition-all ${link.color}`}
          title={link.name}
          data-testid={`share-${link.name.toLowerCase()}`}
        >
          {link.icon}
        </a>
      ))}
      <button
        onClick={handleCopy}
        className="w-9 h-9 rounded-full border border-[#E5E4E0] flex items-center justify-center text-[#5C5C5C] hover:bg-[#D97736] hover:text-white hover:border-[#D97736] transition-all"
        title={language === 'lv' ? 'Kopēt saiti' : 'Copy link'}
        data-testid="share-copy"
      >
        {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

